import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/DraftPlayers.css';
import '../styles/FreeAgency.css';

function FreeAgency() {
  const { id: leagueId } = useParams();
  const { token, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const tours = sportConfig?.tours || [];

  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [nameSearch, setNameSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [adding, setAdding] = useState(null);
  const [dropping, setDropping] = useState(null);
  const [heroLoading, setHeroLoading] = useState(false);
  const [dropConfirm, setDropConfirm] = useState(null); // { kind: 'pick'|'hero', id, playerName, costCredits, pointsSoFar }

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/free-agency/${leagueId}/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setState(data);
      else setError(data.message);
    } catch (err) {
      setError('Failed to load free agency board');
    }
  }, [API_URL, leagueId, token]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/free-agency/${leagueId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setHistory(data.history);
    } catch (err) {
      // history is optional
    }
  }, [API_URL, leagueId, token]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchState(), fetchHistory()]);
      setLoading(false);
    })();
  }, [fetchState, fetchHistory]);

  const handleAdd = async (playerId) => {
    setAdding(playerId);
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/free-agency/${leagueId}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ playerId }),
      });
      const data = await res.json();
      if (data.success) {
        await Promise.all([fetchState(), fetchHistory()]);
      } else {
        setActionError(data.message);
      }
    } catch (err) {
      setActionError('Failed to add player');
    } finally {
      setAdding(null);
    }
  };

  const handlePickHero = async (playerId) => {
    setHeroLoading(true);
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/free-agency/${leagueId}/hero`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ playerId }),
      });
      const data = await res.json();
      if (data.success) {
        await Promise.all([fetchState(), fetchHistory()]);
      } else {
        setActionError(data.message);
      }
    } catch (err) {
      setActionError('Failed to pick hero');
    } finally {
      setHeroLoading(false);
    }
  };

  const handleDrop = async () => {
    if (!dropConfirm) return;
    setActionError(null);
    if (dropConfirm.kind === 'hero') {
      setHeroLoading(true);
      try {
        const res = await fetch(`${API_URL}/free-agency/${leagueId}/hero`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) {
          await Promise.all([fetchState(), fetchHistory()]);
        } else {
          setActionError(data.message);
        }
      } catch (err) {
        setActionError('Failed to drop hero');
      } finally {
        setHeroLoading(false);
        setDropConfirm(null);
      }
      return;
    }

    setDropping(dropConfirm.id);
    try {
      const res = await fetch(`${API_URL}/free-agency/${leagueId}/drop/${dropConfirm.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        await Promise.all([fetchState(), fetchHistory()]);
      } else {
        setActionError(data.message);
      }
    } catch (err) {
      setActionError('Failed to drop player');
    } finally {
      setDropping(null);
      setDropConfirm(null);
    }
  };

  const getCostClass = (cost) => {
    if (cost >= 50) return 'cost-high';
    if (cost >= 40) return 'cost-mid';
    return 'cost-low';
  };

  const sortPlayers = (a, b) => {
    if (b.costCredits !== a.costCredits) return b.costCredits - a.costCredits;
    if (a.ranking !== b.ranking) return a.ranking - b.ranking;
    return a.name.localeCompare(b.name);
  };

  if (loading) return <div className="draft-container"><p className="status-msg">Loading...</p></div>;
  if (error) return <div className="draft-container"><p className="status-msg error">{error}</p></div>;
  if (!state) return null;

  const { freeAgencyOpen, heroMode, available, myPicks, myBudget, myHeroPick, heroPool } = state;

  const filtered = available
    .filter(p => {
      if (filter !== 'ALL' && p.tour !== filter) return false;
      if (nameSearch && !p.name.toLowerCase().includes(nameSearch.toLowerCase())) return false;
      return true;
    })
    .sort(sortPlayers);

  return (
    <div className="draft-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to={`/leagues/${leagueId}`} className="btn btn-secondary">Back to League</Link>
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="draft-content">
        <header className="draft-header">
          <h2>🔓 Free Agency</h2>
          <p>Add and drop players — whoever holds a player when the commissioner scores a round gets all its points.</p>
        </header>

        {!freeAgencyOpen ? (
          <p className="status-msg">Free agency is not currently open for this league.</p>
        ) : (
          <>
            {/* Budget */}
            <div className="budget-section">
              <div className="budget-bar-container">
                <div className="budget-bar" style={{ width: `${Math.max(0, (myBudget.remaining / myBudget.total) * 100)}%` }}></div>
              </div>
              <div className="budget-stats">
                <span>💰 {myBudget.remaining} credits remaining</span>
                <span>{myBudget.spent} / {myBudget.total} spent</span>
                <span>🎯 {myPicks.length} players rostered</span>
              </div>
            </div>

            {actionError && <p className="status-msg error">{actionError}</p>}

            {/* My Roster */}
            {(myPicks.length > 0 || myHeroPick) && (
              <div className="my-team-section">
                <h3>My Roster</h3>
                <table className="my-team-table">
                  <thead>
                    <tr>
                      <th>Tour</th>
                      <th>Player</th>
                      <th>Cost</th>
                      <th>Points</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {myHeroPick && (
                      <tr className="hero-pick-row">
                        <td><span className={`tour-badge ${myHeroPick.player.tour.toLowerCase()}`}>{myHeroPick.player.tour}</span></td>
                        <td className="player-name">{myHeroPick.player.name} <span className="hero-free-badge">HERO</span></td>
                        <td><span className="cost-badge cost-free">HERO</span></td>
                        <td>{myHeroPick.pointsSoFar}</td>
                        <td>
                          <button
                            className="btn btn-small btn-drop"
                            disabled={heroLoading}
                            onClick={() => setDropConfirm({
                              kind: 'hero',
                              playerName: myHeroPick.player.name,
                              pointsSoFar: myHeroPick.pointsSoFar,
                            })}
                          >
                            Drop
                          </button>
                        </td>
                      </tr>
                    )}
                    {myPicks.map(pick => (
                      <tr key={pick._id}>
                        <td><span className={`tour-badge ${pick.player.tour.toLowerCase()}`}>{pick.player.tour}</span></td>
                        <td className="player-name"><a href={`https://google.com/search?q=${encodeURIComponent(pick.player.name)}`} target="_blank" rel="noopener noreferrer">{pick.player.name}</a></td>
                        <td><span className={`cost-badge ${getCostClass(pick.costCredits)}`}>{pick.costCredits} cr</span></td>
                        <td>{pick.pointsSoFar}</td>
                        <td>
                          <button
                            className="btn btn-small btn-drop"
                            disabled={dropping === pick._id}
                            onClick={() => setDropConfirm({
                              kind: 'pick',
                              id: pick._id,
                              playerName: pick.player.name,
                              costCredits: pick.costCredits,
                              pointsSoFar: pick.pointsSoFar,
                            })}
                          >
                            {dropping === pick._id ? '...' : 'Drop'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Hero pool — shown when hero mode is on and manager has no hero */}
            {heroMode && !myHeroPick && (
              <div className="hero-phase-section">
                <h3 className="hero-phase-title">⚡ Pick a Hero</h3>
                <p className="hero-phase-subtitle">Free, scores normally. You can only hold one hero at a time.</p>
                <div className="hero-pool-grid">
                  {heroPool.map(p => (
                    <div key={p._id} className="hero-card">
                      <span className={`tour-badge ${p.tour.toLowerCase()}`}>{p.tour}</span>
                      <span className="hero-card-name">{p.name}</span>
                      <span className="hero-card-rank">Rank #{p.ranking}</span>
                      <span className="hero-card-count">{p.heroPickCount} {p.heroPickCount === 1 ? 'manager' : 'managers'} picked this hero</span>
                      <button
                        className="btn btn-primary btn-hero-pick"
                        disabled={heroLoading}
                        onClick={() => handlePickHero(p._id)}
                      >
                        {heroLoading ? '…' : '⚡ Pick as Hero'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filter bar */}
            <div className="filter-bar">
              <button className={`filter-btn ${filter === 'ALL' ? 'active' : ''}`} onClick={() => setFilter('ALL')}>All Players</button>
              {tours.map(t => (
                <button key={t.id} className={`filter-btn ${filter === t.id ? 'active' : ''}`} onClick={() => setFilter(t.id)}>{t.label}</button>
              ))}
              <div className="search-input-wrap">
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  className="search-input"
                />
                {nameSearch && <button className="search-clear" onClick={() => setNameSearch('')}>&times;</button>}
              </div>
            </div>

            {/* Available players */}
            {filtered.length === 0 ? (
              <p className="status-msg">No available players.</p>
            ) : (
              <div className="players-table-wrap">
                <table className="players-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Tour</th>
                      <th>Player</th>
                      <th>Cost</th>
                      <th>Points so far</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(player => {
                      const canAfford = player.costCredits <= myBudget.remaining;
                      const heroOnly = !!player.isHeroOnly;
                      return (
                        <tr key={player._id} className={heroOnly ? 'hero-only-row' : (!canAfford ? 'cant-afford' : '')}>
                          <td className="rank-cell">#{player.ranking}</td>
                          <td><span className={`tour-badge ${player.tour.toLowerCase()}`}>{player.tour}</span></td>
                          <td className="player-name">
                            <a href={`https://google.com/search?q=${encodeURIComponent(player.name)}`} target="_blank" rel="noopener noreferrer">{player.name}</a>
                            {heroOnly && <span className="hero-only-badge"> ⚡ HERO</span>}
                          </td>
                          <td>{heroOnly ? <span className="cost-badge cost-free">HERO</span> : <span className={`cost-badge ${getCostClass(player.costCredits)}`}>{player.costCredits} cr</span>}</td>
                          <td className="points-cell">{player.pointsSoFar > 0 ? `⭐ ${player.pointsSoFar}` : player.pointsSoFar}</td>
                          <td>
                            {!heroOnly && (
                              <button
                                className="btn btn-small btn-primary"
                                disabled={!canAfford || adding === player._id}
                                onClick={() => handleAdd(player._id)}
                              >
                                {adding === player._id ? '...' : 'Add'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* History */}
            {history.length > 0 && (
              <div className="draft-log-section">
                <h3>📜 Free Agency History</h3>
                <div className="draft-log">
                  {history.map(h => (
                    <div key={h._id} className="draft-log-entry">
                      <span className="pick-manager">{h.manager.firstName || h.manager.username}</span>
                      <span className="pick-arrow">{h.action === 'dropped' || h.action === 'hero-dropped' ? 'dropped' : 'added'}</span>
                      <span className="pick-player"><a href={`https://google.com/search?q=${encodeURIComponent(h.player.name)}`} target="_blank" rel="noopener noreferrer">{h.player.name}</a></span>
                      <span className={`tour-badge ${h.player.tour.toLowerCase()}`}>{h.player.tour}</span>
                      {(h.action === 'hero-picked' || h.action === 'hero-dropped') && <span className="hero-free-badge">HERO</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {dropConfirm && (
        <div className="modal-overlay" onClick={() => setDropConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Drop {dropConfirm.playerName}?</h4>
            <p className="drop-confirm-text">
              {dropConfirm.pointsSoFar > 0
                ? <>You will lose <strong>all {dropConfirm.pointsSoFar} points</strong> earned so far for this player.</>
                : 'This player has not earned any points yet.'}
              {dropConfirm.kind === 'pick' && <> <strong>{dropConfirm.costCredits} credits</strong> will be refunded.</>}
              {dropConfirm.kind === 'hero' && <> You will be able to pick a new hero afterward.</>}
            </p>
            <div className="modal-buttons">
              <button className="btn btn-drop-confirm" onClick={handleDrop}>Yes, Drop Player</button>
              <button className="btn btn-cancel" onClick={() => setDropConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FreeAgency;
